import type { ReactNode } from 'react';
import type {
  AuditScoreResult,
  CoverageLevel,
  HealthGrade,
  PeerSearchResult,
  SampleConfidence,
  ScoreMode,
} from '@/types/gmb';
import { formatMetricDisplayLabel } from '@/pages/gmb-audit/utils/reportDisplay';
import {
  buildDiagnosisDimensions,
  resolveMaxGapDimension,
} from '@/pages/gmb-audit/utils/diagnosisDimensions';

// UI-2A｜Health Overview Dashboard（報告最重要區塊）。
// 唯一資料來源 = Backend AuditScoreResult + peerComparison；前端不重算 score / grade / coverage / priority。
// 語意色 / 版面沿用 UI-1 Design System（background / foreground / primary / secondary / accent）。

const GRADE_FALLBACK_LABEL: Record<HealthGrade, string> = {
  A: '優良',
  B: '良好',
  C: '尚可',
  D: '待加強',
  E: '優先改善',
};

const COVERAGE_SHORT_LABEL: Record<CoverageLevel, string> = {
  COMPLETE: '完整',
  GOOD: '良好',
  LIMITED: '有限',
  INSUFFICIENT: '不足',
};

const CONFIDENCE_LABEL: Record<SampleConfidence, string> = {
  HIGH: '高可信',
  MEDIUM: '中可信',
  LOW: '低可信',
  NONE: '樣本不足',
};

const SCORE_MODE_LABEL: Record<ScoreMode, string> = {
  FULL: '完整健診',
  PARTIAL: '暫定結果',
  INSUFFICIENT: '資料不足',
  CLOSED: '已歇業',
};

function gradeStyle(grade: HealthGrade): string {
  if (grade === 'A' || grade === 'B') return 'bg-primary-100 text-primary-900 ring-1 ring-primary-200';
  if (grade === 'C') return 'bg-accent-100 text-accent-900 ring-1 ring-accent-200';
  return 'bg-accent-200 text-accent-900 ring-1 ring-accent-300';
}

/** Gauge 弧線顏色（A/B 品牌綠、C 琥珀、D/E 深琥珀）。 */
function gaugeColorVar(grade?: HealthGrade): string {
  if (grade === 'A' || grade === 'B') return 'var(--primary-600)';
  if (grade === 'C') return 'var(--accent-500)';
  if (grade === 'D' || grade === 'E') return 'var(--accent-600)';
  return 'var(--primary-600)';
}

function scoreModeChip(mode: ScoreMode): string {
  if (mode === 'FULL') return 'bg-primary-100 text-primary-900 ring-1 ring-primary-200';
  if (mode === 'PARTIAL') return 'bg-accent-100 text-accent-900 ring-1 ring-accent-200';
  if (mode === 'CLOSED') return 'bg-accent-200 text-accent-900 ring-1 ring-accent-300';
  return 'bg-secondary-100 text-secondary-900 ring-1 ring-secondary-200';
}

/** Semi-circle Gauge（純 CSS conic-gradient，不新增任何 library）。 */
function ScoreGauge({ score, grade }: { score: number; grade?: HealthGrade }) {
  const clamped = Math.max(0, Math.min(100, score));
  const arcDeg = Math.round(clamped * 1.8);
  const color = gaugeColorVar(grade);

  return (
    <div
      className="relative mx-auto h-[150px] w-[240px]"
      aria-hidden="true"
    >
      <div className="absolute inset-x-0 top-0 h-[120px] overflow-hidden">
        <div
          className="relative h-[240px] w-[240px] rounded-full"
          style={{
            background: `conic-gradient(from 270deg, oklch(${color}) 0deg ${arcDeg}deg, oklch(var(--background-200)) ${arcDeg}deg 180deg, transparent 180deg 360deg)`,
          }}
        >
          <div className="absolute left-[28px] top-[28px] h-[184px] w-[184px] rounded-full bg-background-50" />
        </div>
      </div>
      <div className="absolute inset-x-0 top-[52px] flex flex-col items-center">
        <span className="text-[3.25rem] font-bold leading-none text-foreground-950 md:text-[3.75rem]">
          {score}
        </span>
        <span className="mt-1 text-base font-medium text-foreground-400">/ 100</span>
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-6">
      <p className="flex items-center gap-2 text-sm font-medium text-foreground-500">
        <i className={`${icon} text-base text-primary-700`} />
        {label}
      </p>
      <div className="mt-2.5 text-2xl font-bold leading-tight text-foreground-950 md:text-[1.75rem]">{value}</div>
      {sub !== undefined && sub !== null && (
        <p className="mt-1.5 text-sm text-foreground-500">{sub}</p>
      )}
    </div>
  );
}

export default function ReportHealthOverview({
  result,
  peerComparison,
}: {
  result: AuditScoreResult;
  peerComparison?: PeerSearchResult;
}) {
  const score = typeof result.score === 'number' ? result.score : undefined;
  const isClosed = result.scoreMode === 'CLOSED';
  const isInsufficient = !isClosed && (result.scoreMode === 'INSUFFICIENT' || score === undefined);

  const dimensions = buildDiagnosisDimensions(result.metrics);
  const maxGap = resolveMaxGapDimension(dimensions);
  const topPriority = (result.priorities || [])[0];

  const coverageShort = COVERAGE_SHORT_LABEL[result.coverageLevel] ?? '不足';

  const sampleCount = peerComparison?.sampleCount;
  const confidenceLabel = peerComparison
    ? CONFIDENCE_LABEL[peerComparison.sampleConfidence] ?? '樣本不足'
    : null;

  let peerValue: ReactNode = '資料不足';
  let peerSub: ReactNode;
  if (typeof sampleCount === 'number') {
    peerValue = `${sampleCount} 家`;
    peerSub = sampleCount > 0 ? confidenceLabel : '暫無可比較同業';
  } else {
    peerSub = '本次未取得同業資料';
  }

  const gradeLabel = result.healthGrade
    ? result.healthLabel || GRADE_FALLBACK_LABEL[result.healthGrade]
    : undefined;

  return (
    <section className="ds-container pt-8 md:pt-10">
      <div className="rounded-2xl border border-background-200 bg-background-100 p-5 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-background-50 text-primary-700 ring-1 ring-primary-200">
              <i className="ri-heart-pulse-line text-xl" />
            </span>
            <div>
              <h2 className="text-xl md:text-2xl">健康度總覽</h2>
              <p className="text-sm text-foreground-500">天地人網 Google 商家公開資料健康度</p>
            </div>
          </div>
          <span className={`ds-badge ring-1 ${scoreModeChip(result.scoreMode)}`}>
            <i className="ri-shield-check-line" />
            {SCORE_MODE_LABEL[result.scoreMode]}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8">
          {/* 左：Score Visual（40%） */}
          <div className="flex flex-col items-center justify-center rounded-2xl border border-background-300 bg-background-50 p-6 lg:col-span-2">
            {isClosed ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-close-circle-line text-2xl" />
                </span>
                <p className="text-base font-bold text-foreground-900">商家已停止營業</p>
                <p className="text-sm leading-relaxed text-foreground-500">
                  目前 Google 公開資料顯示此商家已停止營業。
                </p>
              </div>
            ) : isInsufficient ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-information-line text-2xl" />
                </span>
                <p className="text-base font-bold text-foreground-900">資料不足，暫無法產生完整健康度</p>
                <p className="text-sm leading-relaxed text-foreground-500">
                  評分資料覆蓋率 {result.dataCoveragePercent}%（{coverageShort}），需更多可比較資料才能計分。
                </p>
              </div>
            ) : (
              <>
                <ScoreGauge score={score as number} grade={result.healthGrade} />
                <div className="mt-4 flex flex-col items-center gap-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-base font-medium text-foreground-400">健康度</span>
                    <span className="text-xl font-bold text-foreground-950">
                      {score} <span className="text-sm font-medium text-foreground-400">/ 100</span>
                    </span>
                  </div>
                  {result.healthGrade && gradeLabel && (
                    <span className={`ds-badge ${gradeStyle(result.healthGrade)}`}>
                      <span className="text-base leading-none">{result.healthGrade}</span>
                      <span aria-hidden="true">｜</span>
                      {gradeLabel}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* 右：KPI Cards（60%） */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-3">
            <KpiCard
              icon="ri-database-2-line"
              label="資料覆蓋度"
              value={`${result.dataCoveragePercent}%`}
              sub={coverageShort}
            />
            <KpiCard
              icon="ri-group-line"
              label="同業樣本"
              value={peerValue}
              sub={peerSub}
            />
            <KpiCard
              icon="ri-focus-3-line"
              label="最大缺口"
              value={maxGap ? maxGap.title : '資料不足'}
              sub={maxGap ? `構面完成率 ${Math.round(maxGap.completionRate * 100)}%` : '可評分項目不足'}
            />
            <KpiCard
              icon="ri-arrow-up-circle-line"
              label="最優先改善"
              value={topPriority ? formatMetricDisplayLabel(topPriority.metricKey, '') : '目前無明顯項目'}
              sub={
                topPriority && (topPriority.level === 'HIGH' || topPriority.level === 'MEDIUM')
                  ? topPriority.level === 'HIGH'
                    ? '高優先'
                    : '中優先'
                  : topPriority
                    ? '可持續優化'
                    : '可不需調整'
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}